import { AlertTriangleIcon, ChevronDownIcon, SearchIcon } from "lucide-react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useUsers } from "../../../hooks/useUsers";
import { useLocations } from "../../../hooks/useLocations";
import strings from "../../../i18n/i18nStrings";
import Modal from "../modal/Modal";
import { toast } from "react-toastify";
import { useState, useMemo, useRef, useEffect } from "react";

const UserModal = ({ onClose }: { onClose: () => void }) => {
  const { inviteUser, mutate } = useUsers();
  const { locations } = useLocations();
  const [enableNotifications, setEnableNotifications] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const labelRef = useRef<HTMLLabelElement>(null);

  // Reset dropdown state when modal opens
  useEffect(() => {
    setIsDropdownOpen(false);
    setSearchTerm("");
  }, []);

  const filteredLocations = useMemo(() => {
    if (!locations) return [];
    return locations.filter(location => 
      location.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [locations, searchTerm]);

  const isAllSelected = locations && selectedLocations.length === locations.length;

  const handleSelectAll = () => {
    if (locations) {
      setSelectedLocations(locations.map(loc => loc._id));
    }
  };

  const handleUnselectAll = () => {
    setSelectedLocations([]);
  };

  const handleLocationToggle = (locationId: string) => {
    setSelectedLocations(prev => 
      prev.includes(locationId) 
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  const getDropdownMaxHeight = () => {
    if (!dropdownRef.current) return '12rem'; // Default fallback
    
    const dropdown = dropdownRef.current;
    const modalContent = dropdown.closest('div[class*="max-h-"]');
    
    if (modalContent) {
      const modalRect = modalContent.getBoundingClientRect();
      const dropdownRect = dropdown.getBoundingClientRect();
      
      // Calculate available space from dropdown button to bottom of modal
      const availableHeight = modalRect.bottom - dropdownRect.bottom - 60; // 60px for Add User button
      
      // Ensure minimum height and reasonable maximum
      const maxHeight = Math.max(200, Math.min(availableHeight, 300));
      return `${maxHeight}px`;
    }
    
    return '12rem';
  };

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Auto-scroll to bottom when dropdown opens with custom smooth animation
  useEffect(() => {
    if (labelRef.current) {
      const modalContent = labelRef.current.closest('div[class*="overflow-y-auto"]');
      
      if (modalContent) {
        if (isDropdownOpen) {
          // When dropdown opens: gradually scroll to bottom with custom easing
          setTimeout(() => {
            const startTime = performance.now();
            const startScroll = modalContent.scrollTop;
            const targetScroll = modalContent.scrollHeight - modalContent.clientHeight;
            const duration = 800; // 0.8s to match modal expansion
            
            const easeInOut = (t: number) => {
              return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
            };
            
            const animate = (currentTime: number) => {
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / duration, 1);
              const easedProgress = easeInOut(progress);
              
              modalContent.scrollTop = startScroll + (targetScroll - startScroll) * easedProgress;
              
              if (progress < 1) {
                requestAnimationFrame(animate);
              }
            };
            
            requestAnimationFrame(animate);
          }, 150); // Slightly longer delay to sync with modal expansion
        } else {
          // When dropdown closes: scroll back to top with standard smooth
          modalContent.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }
    }
  }, [isDropdownOpen]);

  interface FormValues {
    first_name: string;
    last_name: string;
    email: string;
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      console.log(data);
      const userData = {
        email: data.email,
        send_device_notifications: enableNotifications,
        location_based_alerts: enableNotifications && selectedLocations.length > 0,
        location_alert_selections: enableNotifications ? selectedLocations : []
      };
      await inviteUser(userData.email, userData.send_device_notifications, userData.location_based_alerts, userData.location_alert_selections);
      mutate();
      onClose();
      toast.success(`${data.email} has been invited`);
    } catch (error) {
      console.error('Error inviting user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  //   const onSubmitUser = () => {
  //     if (userName && userEmail) {
  //       handleAddUser(userName, userEmail, () => {
  //         setNotification(
  //           strings.usersPage.modal.successTitle,
  //           strings.usersPage.modal.successMessage(userName)
  //         );
  //         onClose();
  //       });
  //     }
  //   };

  return (
    <Modal title={strings.usersPage.modal.addUser} onClose={onClose} expanded={isDropdownOpen}>
      <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <p className="text-sm font-medium text-gray-900 mb-4">
          {strings.usersPage.modal.description}
        </p>
        {/* <div className="mb-4">
          <label
            htmlFor="first_name"
            className="px-0 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
          >
            {strings.usersPage.modal.firstNameLabel}
          </label>
          <input
            type="text"
            id="first_name"
            {...register("first_name", { required: true })}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder={strings.usersPage.modal.firstNamePlaceholder}
          />
          {errors.first_name && (
            <span className="text-red-500 text-xs">
              {strings.usersPage.modal.firstNameRequired}
            </span>
          )}
        </div>
        <div className="mb-4">
          <label
            htmlFor="last_name"
            className="px-0 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
          >
            {strings.usersPage.modal.lastNameLabel}
          </label>
          <input
            type="text"
            id="last_name"
            {...register("last_name", { required: true })}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder={strings.usersPage.modal.lastNamePlaceholder}
          />
          {errors.last_name && (
            <span className="text-red-500 text-xs">
              {strings.usersPage.modal.lastNameRequired}
            </span>
          )}
        </div> */}
        <div className="mb-4">
          <label
            htmlFor="email"
            className="px-0 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
          >
            {strings.usersPage.modal.emailLabel}
          </label>
          <input
            type="email"
            id="email"
            {...register("email", {
              required: true,
              pattern: /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
            })}
            className={`mt-1 block w-full ${
              errors.email
                ? "border-2 border-red-500"
                : "border border-gray-300"
            } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base md:text-sm`}
            placeholder={strings.usersPage.modal.emailPlaceholder}
          />
          {errors.email && (
            <div className="flex items-center text-red-700 mt-2">
              <AlertTriangleIcon className="w-4 h-4 mr-2" />
              <span className="font-bold text-xs">
                {strings.usersPage.modal.invalidEmail}
              </span>
            </div>
          )}
        </div>
        
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              id="enableNotifications"
              checked={enableNotifications}
              onChange={(e) => {
                setEnableNotifications(e.target.checked);
                if (!e.target.checked) {
                  setSelectedLocations([]);
                } else {
                  setSelectedLocations(locations?.map(loc => loc._id) || []);
                }
              }}
              className="h-4 w-4 mt-6 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label
              htmlFor="enableNotifications"
              className="ml-2 mt-6 text-xs font-bold text-gray-700 uppercase tracking-wider "
            >
              Enable Device Notifications
            </label>
          </div>
          <p className="text-sm font-medium text-gray-900 mb-4">Get email updates for important device notifications and alerts.</p>
          
          <div className="ml-0 mt-6">
            <label ref={labelRef} className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
              Get Notification for...
            </label>
            

            {locations && locations.length > 0 && (
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  disabled={!enableNotifications}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`mt-1 w-full border rounded-md shadow-sm py-2 px-3 text-left focus:outline-none sm:text-sm flex items-center justify-between ${
                    !enableNotifications 
                      ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed" 
                      : isDropdownOpen
                      ? "border-blue-500 ring-1 ring-blue-500 bg-white"
                      : selectedLocations.length > 0
                      ? "border-blue-300 bg-blue-50 hover:bg-blue-100"
                      : "border-gray-300 bg-white hover:bg-gray-50"
                  }`}
                >
                  <span className="text-sm">
                    {selectedLocations.length === 0 
                      ? "Select locations..." 
                      : isAllSelected
                      ? `All Locations (${locations.length})`
                      : `${selectedLocations.length} location${selectedLocations.length === 1 ? '' : 's'} selected`
                    }
                  </span>
                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
              
              {isDropdownOpen && enableNotifications && (
                <div 
                  className="absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden flex flex-col"
                  style={{ 
                    maxHeight: getDropdownMaxHeight(),
                    top: '100%',
                    marginTop: '4px'
                  }}
                >
                  {/* Search */}
                  <div className="p-2 border-b border-gray-200 flex-shrink-0">
                    <div className="relative">
                      <SearchIcon className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search locations..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  
                  {/* Scrollable content including All Locations and location list */}
                  <div className="flex-1 overflow-y-auto">
                    {/* All/Unselect All */}
                    <div className="p-2 border-b border-gray-200 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isAllSelected}
                            onChange={isAllSelected ? handleUnselectAll : handleSelectAll}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700">All Locations</span>
                        </label>
                        <button
                          type="button"
                          onClick={handleUnselectAll}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Unselect All
                        </button>
                      </div>
                    </div>
                    
                    {/* Location Options */}
                    <div>
                      {filteredLocations.map((location) => (
                        <label
                          key={location._id}
                          className="flex items-center p-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedLocations.includes(location._id)}
                            onChange={() => handleLocationToggle(location._id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">{location.name}</span>
                        </label>
                      ))}
                      {filteredLocations.length === 0 && (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          No locations found
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              </div>
            )}
            
            {(!locations || locations.length === 0) && enableNotifications && (
              <div className="mt-1 p-3 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-md">
                No locations available for notifications.
              </div>
            )}
          </div>
        </div>
        
        {/* Add space when dropdown is open to push buttons below dropdown */}
        {isDropdownOpen && <div className="h-60"></div>}
        
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="mr-3 px-4 py-2 text-sm font-semibold underline text-blue-700 hover:text-blue-800"
          >
            {strings.common.cancel}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-4 py-3 border border-transparent rounded-md shadow-sm text-xs font-bold uppercase tracking-wider text-white ${
              isSubmitting 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-700 hover:bg-blue-800'
            }`}
          >
            {isSubmitting ? 'ADDING...' : strings.usersPage.modal.addUser.toUpperCase()}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default UserModal;
